import {
  useActivitiesStore,
  useAddActivitiesStore,
} from "@/stores/activities.module";
import {
  useActivityCategoriesStore,
  useAddActivityCategoriesStore,
  useAddMeetingsCategoriesStore,
  useAddNewsCategoriesStore,
  useMeetingsCategoriesStore,
  useNewsCategoriesStore,
} from "@/stores/categories.module";
import {
  useAddEmployeeStore,
  useEmployeesStore,
} from "@/stores/employees.module";
import {
  useAddEmployeeTitlesStore,
  useEmployeeTitlesStore,
} from "@/stores/employeeTitles.module";
import { useAddNewsStore, useNewsStore } from "@/stores/news.module";
import {
  useCoreNewsStore,
  useAddCoreNewsStore,
} from "@/stores/coreNews.module";
import { useAuthorsStore, useAddAuthorsStore } from "@/stores/authors.module";
import { usePlannerTemplateForm } from "@/stores/plannerTemplateForm.module";
import type { StoreName } from "@/types";
import type { Store } from "pinia";
import { useAddMeetingStore, useMeetingsStore } from "@/stores/meetings.module";
import {
  useAddMeetingsAgendaPointsStore,
  useMeetingsAgendaPointsStore,
} from "@/stores/meetingAgendaPoints.module";

let STORE_MAP: Record<StoreName, Store | null> = {
  NEWS: null,
  ADD_NEWS: null,
  ACTIVITIES: null,
  ADD_ACTIVITIES: null,
  MEETINGS: null,
  ADD_MEETINGS: null,
  EMPLOYEES: null,
  EMPLOYEES_TITLES: null,
  EMPLOYEES_ADD_TITLES: null,
  ADD_EMPLOYEES: null,
  ACTIVITIES_CATEGORIES: null,
  ACTIVITIES_ADD_CATEGORIES: null,
  NEWS_CATEGORIES: null,
  NEWS_ADD_CATEGORIES: null,
  MEETINGS_CATEGORIES: null,
  MEETINGS_ADD_CATEGORIES: null,
  MEETINGS_AGENDA_POINTS: null,
  MEETINGS_ADD_AGENDA_POINTS: null,
  CORE_NEWS: null,
  ADD_CORE_NEWS: null,
  AUTHORS: null,
  ADD_AUTHORS: null,
  PLANNER_TEMPLATE_FORM: null,
};

/**
 * We should set the all stores in a MAP so that we can access them by using
 * their name.
 *
 * Check the `getStore()` function below.
 */
export const setStores = () => {
  STORE_MAP = {
    NEWS: useNewsStore(),
    ADD_NEWS: useAddNewsStore(),
    ACTIVITIES: useActivitiesStore(),
    ADD_ACTIVITIES: useAddActivitiesStore(),
    MEETINGS: useMeetingsStore(),
    ADD_MEETINGS: useAddMeetingStore(),
    EMPLOYEES: useEmployeesStore(),
    ADD_EMPLOYEES: useAddEmployeeStore(),
    EMPLOYEES_TITLES: useEmployeeTitlesStore(),
    EMPLOYEES_ADD_TITLES: useAddEmployeeTitlesStore(),
    ACTIVITIES_CATEGORIES: useActivityCategoriesStore(),
    ACTIVITIES_ADD_CATEGORIES: useAddActivityCategoriesStore(),
    NEWS_CATEGORIES: useNewsCategoriesStore(),
    NEWS_ADD_CATEGORIES: useAddNewsCategoriesStore(),
    MEETINGS_CATEGORIES: useMeetingsCategoriesStore(),
    MEETINGS_ADD_CATEGORIES: useAddMeetingsCategoriesStore(),
    MEETINGS_AGENDA_POINTS: useMeetingsAgendaPointsStore(),
    MEETINGS_ADD_AGENDA_POINTS: useAddMeetingsAgendaPointsStore(),
    CORE_NEWS: useCoreNewsStore(),
    ADD_CORE_NEWS: useAddCoreNewsStore(),
    AUTHORS: useAuthorsStore(),
    ADD_AUTHORS: useAddAuthorsStore(),
    PLANNER_TEMPLATE_FORM: usePlannerTemplateForm(),
  };
};

/**
 * We do this because there are some component that uses different stores.
 *
 * For example, let assume that you have a Form.vue component. We want to use
 * same component for editing and adding new form value. We want to create a new
 * modal (Dialog) to add some data while the editing version is on the back.
 *
 * In this case, Form.vue should be able to use different pinia stores.
 *
 * Check the `news.module.js` file. You can see the same schema is used in two
 * different stores. If you search the :is="NEWS_TAB_MAP[newsStore.currentTab]"
 * value in the project, you can see that we allow to components to use different
 * stores.
 *
 * <Form storeName="NEWS" />
 *
 * Inside the Form.vue;
 * const store = getStore(props.storeName);
 */
export const getStore = <T>(name: StoreName) => {
  const store = STORE_MAP[name] as T;

  if (store) {
    return store;
  }

  setStores();
  return STORE_MAP[name] as T;
};

export const getStoreOrNull = <T>(name: StoreName) => {
  const store = STORE_MAP[name] as T;

  if (store) {
    return store;
  }

  return null;
};
